import { bucketTransfersByTime, type ActivityBucket } from '../lib/activityBuckets';
import type { Transfer } from '../lib/types';
import styles from './ActivityHistogram.module.css';

function formatRange(bucket: ActivityBucket): string {
  const start = new Date(bucket.startMs);
  const end = new Date(bucket.endMs);
  const fmt = (d: Date) =>
    d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

export interface ActivityHistogramProps {
  transfers: Transfer[];
  selectedBucketIndex: number | null;
  onSelectBucket: (index: number | null) => void;
}

/**
 * A scan aid over the transfers already loaded on the client — never a
 * complete-period total. Clicking a bar filters the feed to that time
 * slice; pending transfers (no timestamp) always stay visible regardless
 * of the selection, since they can't be placed on this timeline.
 */
export function ActivityHistogram({
  transfers,
  selectedBucketIndex,
  onSelectBucket,
}: ActivityHistogramProps) {
  const buckets = bucketTransfersByTime(transfers);

  if (buckets.length === 0) return null;

  const maxCount = Math.max(...buckets.map((b) => b.count), 1);
  const selected = selectedBucketIndex !== null ? buckets[selectedBucketIndex] : null;
  const timedCount = buckets.reduce((sum, b) => sum + b.count, 0);

  return (
    <div className={styles.wrapper}>
      <div className={styles.bars} role="group" aria-label="Filter by time range">
        {buckets.map((bucket) => {
          const isSelected = bucket.index === selectedBucketIndex;
          const heightPct = Math.max(
            (bucket.count / maxCount) * 100,
            bucket.count > 0 ? 12 : 3,
          );
          return (
            <button
              key={bucket.index}
              type="button"
              className={isSelected ? `${styles.bar} ${styles.barSelected}` : styles.bar}
              style={{ height: `${heightPct}%` }}
              disabled={bucket.count === 0}
              aria-pressed={isSelected}
              aria-label={`${formatRange(bucket)}: ${bucket.count} transfer${bucket.count === 1 ? '' : 's'}${isSelected ? ', selected' : ''}`}
              onClick={() => onSelectBucket(isSelected ? null : bucket.index)}
            />
          );
        })}
      </div>
      <div className={styles.caption}>
        <span>
          {selected
            ? `Selected: ${formatRange(selected)} · ${selected.count} of ${timedCount} loaded transfers`
            : `Last ${timedCount} transfers with a timestamp · tap a bar to list that period`}
        </span>
        {selected && (
          <button
            type="button"
            className={styles.reset}
            onClick={() => onSelectBucket(null)}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
